import { cloneDeep, forEach, get, has, map, set } from 'lodash';

import Ivr from 'src/models/Ivrs/Ivr';
import IvrNode from 'src/models/Ivrs/IvrNode';
import { IvrTreeData } from 'src/models/Ivrs/IvrTreeData';
import { NODE_LINKS_MAPPER } from 'src/models/Ivrs/NodeListMapper';

/**
 * Returns new ivr configuration with fixed invalid node IDs
 * (node id should be alphanumeric, the '_' is allowed)
 *
 * @param { Ivr } ivrConfig The configuration that need to be fixed....
 */
const replaceNotValidIvrIds = (ivrConfig: Ivr, treeData?: IvrTreeData): [Ivr, IvrTreeData | undefined] => {
  const ids = map(ivrConfig.nodes, node => node.ivrNode.id);

  const ivrConfigCloned = cloneDeep(ivrConfig);
  const treeDataCloned = cloneDeep<IvrTreeData | undefined>(treeData);

  if (!ids.length) {
    return [ivrConfig, treeData];
  }

  if (ivrConfigCloned.rootNodeId && !/^[a-z0-9_]+$/i.test(ivrConfigCloned.rootNodeId)) {
    ivrConfigCloned.rootNodeId = ivrConfigCloned.rootNodeId.replace(/-/g, '_');
  }

  ids.forEach(id => {
    if (!/^[a-z0-9_]+$/i.test(id)) {
      const newId = id.replace(/-/g, '_');

      if (ivrConfigCloned.rootNodeId === id) {
        ivrConfigCloned.rootNodeId = newId;
      }

      if (treeDataCloned && has(treeDataCloned.positions, id)) {
        treeDataCloned.positions[newId] = treeDataCloned.positions[id];
        delete treeDataCloned.positions[id];
      }

      forEach(ivrConfig.nodes, (node: IvrNode) => {
        if (node.ivrNode.id === id) {
          set(node, 'ivrNode.id', newId);
        } else {
          const fieldNames = get(NODE_LINKS_MAPPER, node.type, []);

          fieldNames.forEach(fieldName => {
            if (typeof fieldName === 'object') {
              const items = get(node, `ivrNode.${fieldName.prefixPath}`, []);
              items.forEach((_, index) => {
                const attr = `ivrNode.${fieldName.prefixPath}.${index}.${fieldName.postfixPath}`;
                const nextNodeId = get(node, attr);

                if (nextNodeId && nextNodeId === id) {
                  set(node, attr, newId);
                }
              });
            } else {
              const attr = `ivrNode.${fieldName}`;
              const nextNodeId = get(node, attr);

              if (nextNodeId && nextNodeId === id) {
                set(node, attr, newId);
              }
            }
          });
        }
      });
    }
  });

  return [ivrConfigCloned, treeDataCloned];
};

export default replaceNotValidIvrIds;
